import { useEffect, useMemo } from 'react'

import { useCADQuery } from '@/services/queries/cads'
import { useOperationSteps } from '@/services/queries/operation_steps'
import {
  countAllParts,
  findPartsInOperation,
} from '../fns/findPartsInOperation'

import { type BillOfMaterials, type ToolsBillOfMaterials } from '@/state'
import {
  partsTablesQuery,
  toolsTablesQuery,
} from '@/services/hooks/template_attributes'
import { MUTATION_KEYS as CAD_VERSIONS_MUTATION_KEYS } from '@/services/queries/cad_versions'
import { MUTATION_KEYS as DOCUMENT_PAGE_MUTATION_KEYS } from '@/services/queries/document_pages'
import { MUTATION_KEYS as TEMPLATE_ATTRIBUTES_MUTATION_KEYS } from '@/services/queries/template_attributes'
import { useMutationState, useQuery } from '@tanstack/react-query'
import queryClient from '@/queryClient'
import { Step } from '@/services/queries/operation_steps/types'

const TABLES_MUTATION_KEYS = [
  CAD_VERSIONS_MUTATION_KEYS.UPDATE_CAD_VERSION,
  DOCUMENT_PAGE_MUTATION_KEYS.DELETE_DOCUMENT_PAGE,
]

const isMutationForTableType = ({
  mutation,
  tableType,
}: {
  mutation: any
  tableType: 'parts-table' | 'tools-table'
}) => {
  const [mutationKey] = mutation.options.mutationKey || []

  if (!mutationKey) return false
  if (TABLES_MUTATION_KEYS.includes(mutationKey as any)) return true

  const stateVars = mutation.state.variables as any | undefined
  const tableTypeInPayload = stateVars?.payload?.template_values?.type
  if (tableTypeInPayload === tableType) return true

  const bulkTableTypes = (stateVars?.payloads || [])
    .map((payload: any) => payload?.template_values?.type)
    .filter(Boolean)
  if (bulkTableTypes.includes(tableType)) return true

  if (
    mutationKey ===
    TEMPLATE_ATTRIBUTES_MUTATION_KEYS.BULK_DELETE_TEMPLATE_ATTRIBUTES
  ) {
    const deletedTypes = (stateVars?.templateAttributes || [])
      .map((templateAttribute: any) => templateAttribute?.template_values?.type)
      .filter(Boolean)
    return deletedTypes.includes(tableType)
  }

  if (
    mutationKey === TEMPLATE_ATTRIBUTES_MUTATION_KEYS.DELETE_TEMPLATE_ATTRIBUTE
  ) {
    const deletedType = (mutation.state.data as any)?.oldTemplateAttribute
      ?.template_values?.type
    return deletedType === tableType
  }

  return false
}

export const useStepBOM = ({
  documentPageId,
}: {
  documentPageId?: string | null
}): BillOfMaterials => {
  const { data: cadData } = useCADQuery()

  const { isLoading: isLoadingSteps, steps } = useOperationSteps()
  const assemblyTree = useMemo(
    () => cadData.rawAssemblyTree,
    [cadData.rawAssemblyTree],
  )

  const stepBom = useMemo(() => {
    if (!assemblyTree || isLoadingSteps || !steps || !documentPageId) {
      return []
    }

    if (!assemblyTree.nodes) {
      return []
    }

    const step = steps.find((step) => step.id === documentPageId)

    if (!step) {
      console.warn(
        'Unable to find parts in operation: Operation step not found',
      )
      return []
    }

    return findPartsInOperation(step, assemblyTree)
  }, [assemblyTree, isLoadingSteps, documentPageId, steps])

  return stepBom
}

export const useListCustomPartsBOM = ({
  steps,
  isLoading,
}: {
  steps: Array<Step>
  isLoading: boolean
}): Record<string, number> => {
  const customPartsQuery = useQuery({
    queryKey: ['custom_parts'],
    enabled: !isLoading,
    queryFn: async () => {
      if (steps.length === 0) return {}
      const partsTables = await Promise.all(
        steps.reduce(
          (accum, step) => accum.concat(partsTablesQuery(step.id)),
          [] as any,
        ),
      ).then((res) => res.flat())

      const customParts = partsTables.map(
        (table) => table?.template_values.rows || [],
      )

      return customParts?.reduce((accum, pageCustomParts) => {
        pageCustomParts.forEach((part) => {
          const { partName, quantity } = part
          if (partName) {
            const finalQuantity =
              !quantity || isNaN(Number(quantity)) ? 0 : Number(quantity)
            if (accum[partName]) {
              accum[partName] += finalQuantity
            } else {
              accum[partName] = finalQuantity
            }
          }
        })
        return accum
      }, {} as any)
    },
  })

  const tableMutationData = useMutationState({
    filters: {
      status: 'success',
      predicate: (mutation) => {
        return isMutationForTableType({
          mutation,
          tableType: 'parts-table',
        })
      },
    },
    select: (mutation) => mutation.state.submittedAt,
  })
  const latestPartsTableMutationAt = useMemo(
    () => Math.max(0, ...tableMutationData),
    [tableMutationData],
  )

  useEffect(() => {
    if (!latestPartsTableMutationAt) return

    const invalidateQueries = async () =>
      queryClient.invalidateQueries({
        queryKey: ['custom_parts'],
      })

    invalidateQueries().catch((error) => {
      console.error(error)
    })
  }, [latestPartsTableMutationAt])

  return customPartsQuery.data || []
}

export const useFullPartsBOM = (args?: {
  onlyCountUsed?: boolean
}): BillOfMaterials => {
  const { data: cadData } = useCADQuery()
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const assemblyTree = useMemo(() => cadData?.rawAssemblyTree, [cadData])
  const customParts = useListCustomPartsBOM({
    steps: steps || [],
    isLoading: isLoadingSteps,
  })
  const usedPartIds = useMemo(
    () => steps?.flatMap((step) => step.assembly_group_ids) || [],
    [steps],
  )

  if (!assemblyTree || isLoadingSteps || !steps) return []

  return countAllParts({
    assemblyTree,
    customParts,
    usedPartIds,
    onlyCountUsed: args?.onlyCountUsed,
  })
}

export const useFullToolsBOM = (): ToolsBillOfMaterials => {
  const { isLoading: isLoadingSteps, steps } = useOperationSteps()

  const customToolsQuery = useQuery({
    queryKey: ['custom_tools'],
    enabled: !isLoadingSteps,
    queryFn: async () => {
      if (steps.length === 0) return []
      const toolsTables = await Promise.all(
        steps.reduce(
          (accum, step) => accum.concat(toolsTablesQuery(step.id)),
          [] as any,
        ),
      ).then((res) => res.flat())

      const customTools = toolsTables.map(
        (table) => table?.template_values.rows || [],
      )

      return customTools?.reduce((accum, pageCustomParts) => {
        pageCustomParts.forEach((tool) => {
          const { toolName } = tool
          const childInBom = accum.find((bom) => bom.toolName === toolName)
          if (!childInBom) {
            accum.push({ toolName })
          }
        })
        return accum
      }, [] as any)
    },
  })

  const tableMutationData = useMutationState({
    filters: {
      status: 'success',
      predicate: (mutation) => {
        return isMutationForTableType({
          mutation,
          tableType: 'tools-table',
        })
      },
    },
    select: (mutation) => mutation.state.submittedAt,
  })
  const latestToolsTableMutationAt = useMemo(
    () => Math.max(0, ...tableMutationData),
    [tableMutationData],
  )

  useEffect(() => {
    if (!latestToolsTableMutationAt) return

    const invalidateQueries = async () => {
      await queryClient.invalidateQueries({
        queryKey: ['custom_tools'],
      })
    }

    invalidateQueries().catch((error) => {
      console.error(error)
    })
  }, [latestToolsTableMutationAt])

  if (isLoadingSteps || !steps) return []

  return customToolsQuery.data || []
}
